import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { ChevronDown } from "lucide-react";
import { RichTextEditor } from "@/components/RichTextEditor";
import { TagCategoryPicker } from "@/components/excerpts/TagCategoryPicker";
import { TagsManagerSheet } from "@/components/excerpts/TagsManagerSheet";
import { CodexTopicPicker } from "@/components/excerpts/CodexTopicPicker";
import { AddSourceInlineDialog } from "@/components/library/AddSourceInlineDialog";
import { UpgradeDialog } from "@/components/UpgradeDialog";

import { AppSeo } from "@/components/AppSeo";
import { useExcerptEditor } from "@/components/excerpts/editor/useExcerptEditor";
import {
  InheritedCategoriesSection,
  LinkedSourceBanner,
  SourcePickerSection,
} from "@/components/excerpts/editor/SourceSections";
import { UnlinkSourceDialog } from "@/components/excerpts/editor/UnlinkSourceDialog";
import { useIsMobile } from "@/hooks/use-mobile";

const ExcerptEditor = () => {
  const e = useExcerptEditor();
  const isMobile = useIsMobile();

  const advancedFields = (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div><Label>Page (start)</Label><Input type="number" inputMode="numeric" value={e.pageStart} onChange={(ev) => e.setPageStart(ev.target.value)} /></div>
        <div><Label>Page (end)</Label><Input type="number" inputMode="numeric" value={e.pageEnd} onChange={(ev) => e.setPageEnd(ev.target.value)} /></div>
      </div>
      <div>
        <Label>Source URL (optional, for this specific excerpt)</Label>
        <Input value={e.sourceUrl} onChange={(ev) => e.setSourceUrl(ev.target.value)} inputMode="url" autoCapitalize="off" autoCorrect="off" />
      </div>
      <InheritedCategoriesSection libraryItemId={e.libraryItemId} inheritedCategories={e.inheritedCategories} />
      <div>
        <Label>Notes</Label>
        <Textarea value={e.notes} onChange={(ev) => e.setNotes(ev.target.value)} rows={3} />
      </div>
    </div>
  );

  return (
    <>
      <AppSeo title="Edit excerpt" />
      <div className="max-w-3xl space-y-6 pb-24 md:pb-0">
        <h1 className="font-serif text-2xl font-semibold sm:text-3xl">{e.isEdit ? "Edit excerpt" : "New excerpt"}</h1>

        {e.selectedLibraryItem ? (
          <LinkedSourceBanner selectedLibraryItem={e.selectedLibraryItem} returnTo={e.returnTo} />
        ) : null}

        <Card className="shadow-soft">
          <CardContent className="py-6 space-y-5">
            <div>
              <Label>Excerpt</Label>
              <RichTextEditor value={e.bodyHtml} onChange={(html, text) => { e.setBodyHtml(html); e.setBodyText(text); }} />
              <p className="text-xs text-muted-foreground mt-1">Bold and italic supported. Paste freely — Greek, Hebrew, Sanskrit and other scripts are preserved.</p>
            </div>

            {e.active && (
              <SourcePickerSection
                selectedLibraryItem={e.selectedLibraryItem}
                returnTo={e.returnTo}
                libraryItemId={e.libraryItemId}
                setLibraryItemId={e.setLibraryItemId}
                comboboxOptions={e.comboboxOptions}
                citationPreview={e.citationPreview}
                citationIncomplete={e.citationIncomplete}
                inheritedCategories={e.inheritedCategories}
                handleOpenAddSource={e.handleOpenAddSource}
                handleOpenEditSource={e.handleOpenEditSource}
                openUnlinkDialog={e.openUnlinkDialog}
              />
            )}

            {e.active && (
              <>
                <div className="space-y-2">
                  <Label>Tags</Label>
                  <TagCategoryPicker
                    kind="tags"
                    workspaceId={e.active.id}
                    value={e.tags}
                    onChange={e.setTags}
                    onManage={() => e.setTagsManagerOpen(true)}
                  />
                </div>
                {e.active.codex_enabled !== false && (
                  <div className="space-y-2">
                    <Label>Topics</Label>
                    <CodexTopicPicker workspaceId={e.active.id} value={e.topics} onChange={e.setTopics} />
                    <p className="text-[11px] text-muted-foreground">
                      Topics come from your Codex Framework Topic Tree. Add new topics in Codex → Framework.
                    </p>
                  </div>
                )}
                <TagsManagerSheet open={e.tagsManagerOpen} onOpenChange={e.setTagsManagerOpen} />
              </>
            )}

            {isMobile ? (
              <details className="group rounded-md border border-border bg-muted/30 px-3 py-2">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-2 text-sm font-medium">
                  <span>More options</span>
                  <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
                </summary>
                <div className="mt-4">{advancedFields}</div>
              </details>
            ) : (
              advancedFields
            )}

            {/* Desktop action bar */}
            <div className="hidden justify-end gap-2 md:flex">
              <Button variant="ghost" onClick={() => (e.returnHref ? e.navigate(e.returnHref) : e.navigate(-1))}>{e.returnTo ? "Return to my source/excerpt view" : "Cancel"}</Button>
              <Button onClick={e.save} disabled={e.loading || e.blockedByLimit}>{e.loading ? "Saving…" : "Save excerpt"}</Button>
            </div>
          </CardContent>
        </Card>

        <UpgradeDialog open={e.upgradeOpen} onOpenChange={e.setUpgradeOpen} tier="Pro" feature="Unlimited excerpts" />

        {e.active && (
          <AddSourceInlineDialog
            open={e.addSourceOpen}
            onOpenChange={(next) => {
              e.setAddSourceOpen(next);
              if (!next) e.setEditSourceItem(null);
            }}
            workspaceId={e.active.id}
            initialQuery={e.addSourceQuery}
            initialIdentifier={e.addSourceIdentifier}
            editingItem={e.editSourceItem}
            existingItems={e.libraryItems}
            onCreated={e.handleSourceSaved}
          />
        )}

        <UnlinkSourceDialog
          open={e.unlinkOpen}
          onOpenChange={e.setUnlinkOpen}
          isEdit={e.isEdit}
          selectedLibraryItem={e.selectedLibraryItem}
          otherExcerptCount={e.otherExcerptCount}
          unlinkAlsoDelete={e.unlinkAlsoDelete}
          setUnlinkAlsoDelete={e.setUnlinkAlsoDelete}
          unlinking={e.unlinking}
          performUnlink={e.performUnlink}
        />
      </div>

      {/* Mobile sticky save bar */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-background/95 px-3 py-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)] backdrop-blur supports-[backdrop-filter]:bg-background/80 md:hidden">
        <div className="mx-auto flex max-w-3xl items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => (e.returnHref ? e.navigate(e.returnHref) : e.navigate(-1))}>
            {e.returnTo ? "Back" : "Cancel"}
          </Button>
          <Button onClick={e.save} disabled={e.loading || e.blockedByLimit} className="flex-1 sm:flex-none">
            {e.loading ? "Saving…" : "Save excerpt"}
          </Button>
        </div>
      </div>
    </>
  );
};

export default ExcerptEditor;
